import { useMutation } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import type { ChannelProviderId, MarketplaceAccountView, MarketplaceMode } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { CHANNEL_PROVIDER_IDS, marketplaceThe } from "@cx/domain";
import { ContentHeader, Field, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { AccountCard } from "./MarketplaceSettingsPage";
import { PROVIDER_UI } from "./labels";
import { useMarketplaceAccounts } from "./useAccounts";

/** O que cada canal traz (T3.3). */
const ABOUT: Record<ChannelProviderId, string> = {
  nuvemshop:
    "Loja virtual: produtos e variantes, pedidos pagos (avisos da loja em tempo real) e estoque/preço por variante.",
  shopify:
    "Loja virtual: produtos e variantes, pedidos pagos (avisos da loja em tempo real) e estoque no local principal.",
  magalu:
    "Marketplace: SKUs do portfólio, pedidos aprovados (busca a cada 10 minutos) e estoque/preço por SKU.",
  amazon:
    "Marketplace (Amazon.com.br): SKUs, pedidos (busca a cada 10 minutos; FBA sem reserva no ERP) e estoque/preço por SKU.",
};

const MODE: Record<MarketplaceMode, readonly ["success" | "info" | "secondary", string]> = {
  live: ["success", "Habilitado"],
  sandbox: ["info", "Testes"],
  disabled: ["secondary", "Desligado"],
};

/** Preferências › Integrações › Canais de venda: Nuvemshop, Shopify, Magalu e Amazon (RF-VEN-09). */
export function ChannelsPage(): ReactElement {
  const list = useMarketplaceAccounts();
  return (
    <>
      <ContentHeader
        title="Integrações · Canais de venda"
        breadcrumb={["Preferências", "Integrações", "Canais"]}
      />
      <QueryStatus loading={list.isLoading} error={list.error} />
      {list.isSuccess &&
        CHANNEL_PROVIDER_IDS.map((p) => (
          <ChannelSection
            key={p}
            provider={p}
            mode={list.data.modes?.[p] ?? "disabled"}
            accounts={list.data.accounts.filter((a) => a.provider === p)}
          />
        ))}
    </>
  );
}

function ChannelSection({
  provider: p,
  mode,
  accounts,
}: {
  provider: ChannelProviderId;
  mode: MarketplaceMode;
  accounts: MarketplaceAccountView[];
}): ReactElement {
  const { me, api } = useSession();
  const canCreate = can(me.permissions as Permissions, "marketplaces", "create");
  const toast = useToast();
  const ui = PROVIDER_UI[p];
  const [shop, setShop] = useState("");
  const [shopError, setShopError] = useState<string | undefined>();
  const connect = useMutation({
    mutationFn: () =>
      api<{ url: string }>(`/marketplaces/channels/${p}/authorize`, {
        method: "POST",
        body: JSON.stringify(p === "shopify" ? { shop } : {}),
      }),
    onSuccess: (r) => {
      window.location.assign(r.url);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields?.shop) setShopError(e.fields.shop);
      toast("danger", errorMessage(e));
    },
  });
  const [tone, label] = MODE[mode];
  const unit = ui.unit.toLowerCase();
  /** "a Nuvemshop", "o Magalu". */
  const the = marketplaceThe(p).charAt(0).toLowerCase() + marketplaceThe(p).slice(1);
  return (
    <section className="mb-4" aria-label={`Canal ${ui.label}`}>
      <div className="box card mb-2">
        <div className="card-header d-flex flex-wrap align-items-center gap-2">
          <h2 className="h6 mb-0">{ui.label}</h2>
          <StatusBadge tone={tone}>{label}</StatusBadge>
          <span className="small text-muted">
            {accounts.length} {accounts.length === 1 ? unit : `${unit}s`} conectada
            {accounts.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="card-body">
          <p className="small mb-2">{ABOUT[p]}</p>
          {mode === "sandbox" && (
            <div className="alert alert-info py-2">
              Ambiente de testes: a conexão usa {the} de testes (nenhuma {unit} real é acessada).
            </div>
          )}
          {mode === "disabled" && (
            <div className="alert alert-warning py-2 mb-0">
              A integração com {the} não está habilitada neste ambiente.
            </div>
          )}
          {canCreate && mode !== "disabled" && (
            <form
              className="row align-items-end"
              onSubmit={(e) => {
                e.preventDefault();
                setShopError(undefined);
                connect.mutate();
              }}
            >
              {p === "shopify" && (
                <Field
                  label="Endereço da loja Shopify"
                  required
                  className="col-md-6"
                  error={shopError}
                  hint="minhaloja ou minhaloja.myshopify.com"
                >
                  <input value={shop} maxLength={120} onChange={(e) => setShop(e.target.value)} />
                </Field>
              )}
              <div className="col-auto mb-3">
                <button type="submit" className="btn btn-primary" disabled={connect.isPending}>
                  Conectar {unit} {ui.label}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      {accounts.map((a) => (
        <AccountCard key={a.id} account={a} />
      ))}
    </section>
  );
}
